import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Autocomplete,
  Stack,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  LibraryAdd as AddMultiple,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { useTSQDynamicList } from "../../hooks/useTSDynamicQuery";
import {
  useTSMApproveComplaint,
  useTSMPostProcessComplaint,
} from "../../hooks/useTSQualityRecords";
import { paginationDefaults } from "../../config/settingsDefaults";
import {
  RMA_FULL,
  RMA_STATUSES,
  RMAStatusType,
  RMA_REASONS,
  RMAReasonType,
  RMAFormMode,
} from "../../types/qualityRecordTypes";
import {
  DynamicRequestFilter,
  DynamicRequestSort,
} from "../../types/dynamicQueryTypes";
import { useAuth } from "../../contexts/AuthContextWithQuery";
import { ItemIcon } from "../../components/common/NucleusSymbolsAndIcons";
import RMAHandlerForm from "./RMADialog";
import ProgressIndicator, {
  ProgressIndicatorProps,
} from "../../components/common/ProgressIndicator";
import { isDev } from "../../config/environment";
import SplitButton from "../../components/common/SplitButton";
import PageHeaderComponent from "../../components/common/PageHeader";

const FilterBox: React.FC<{
  finalSet: React.Dispatch<React.SetStateAction<string>>;
  placeholder: string;
}> = ({ finalSet, placeholder }) => {
  const [localVal, setLocalVal] = useState<string>("");
  return (
    <TextField
      label="Search"
      value={localVal}
      onChange={(e) => setLocalVal(e.target.value)}
      onBlur={(e) => finalSet(e.target.value)}
      placeholder={placeholder}
      slotProps={{
        input: {
          startAdornment: (
            <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
          ),
        },
      }}
      sx={{ minWidth: 300 }}
    />
  );
};

const RMARow: React.FC<{
  rma: RMA_FULL;
  onClickReceive: () => void;
  onClickView: () => void;
  onClickEdit: () => void;
}> = ({ rma, onClickReceive, onClickView, onClickEdit }) => {
  const ProgressStatus: ProgressIndicatorProps = {
    steps: [
      {
        label: "Confirmation",
      },
      {
        label: "Receipt",
      },
      {
        label: "Inspection",
      },
      {
        label: "Service",
      },
      {
        label: "Review",
      },
      {
        label: "Disposition",
      },
    ],
    activeStep: (() => {
      switch (rma.status) {
        case "AWAITING_CENTESE_CONFIRMATION":
        case "AWAITING_CUSTOMER_CONFIRMATION":
          return 0;
        case "AWAITING_RECEIPT":
          return 1;
        case "AWAITING_INSPECTION":
        case "INSPECTION_IN_PROGRESS":
          return 2;
        case "AWAITING_REPAIR":
        case "REPAIRS_IN_PROGRESS":
          return 3;
        case "AWAITING_REVIEW":
          return 4;
        case "AWAITING_SHIPMENT":
          return 5;
        case "COMPLETED":
          return 6;
      }
    })(),
    statusOverride: rma.is_voided
      ? "void"
      : rma.is_holding
        ? "hold"
        : undefined,
  };

  function formatRMANumber(number: number, year: number): string {
    return `${year.toString().slice(2)}-${number.toString().padStart(4, "0")}`;
  }

  return (
    <React.Fragment>
      <TableRow key={rma.id} hover>
        <TableCell align="left">
          <Stack direction="column" gap={0.5}>
            <Typography variant="body2" sx={{ color: "text.primary" }}>
              RMA: {formatRMANumber(rma.number, rma.date_opened.getFullYear())}
            </Typography>
            {rma.nuc_cf_num && (
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                {rma.nuc_cf_num}
              </Typography>
            )}
            {rma.gl_cf && (
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                GL: {rma.gl_cf}
              </Typography>
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Stack direction="column" gap={0.5}>
            <Typography variant="body2" sx={{ color: "text.primary" }}>
              {rma.cust_code} {rma.cust_name}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.primary" }}>
              {rma.contact.name}
              <br />
              {rma.contact.email ? (
                <a
                  href={`mailto:${rma.contact.email}`}
                  color={"inherit"}
                  rel={"noreferrer"}
                  target={"_blank"}
                >
                  {rma.contact.email}
                </a>
              ) : (
                "No Email Provided"
              )}
              <br />
              {rma.contact.phone ? rma.contact.phone : "No Phone Provided"}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell align="left">
          <Stack direction="column" spacing={1}>
            <Typography variant="body2" sx={{ color: "text.primary" }}>
              Opened: {rma.date_opened.toLocaleDateString("en-US")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.primary" }}>
              Received:{" "}
              {rma.date_received
                ? rma.date_received.toLocaleDateString("en-US")
                : "N/A"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.primary" }}>
              Closed:{" "}
              {rma.date_closed
                ? rma.date_closed.toLocaleDateString("en-US")
                : "N/A"}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell sx={{ verticalAlign: "top" }}>
          <Stack direction="column" spacing={1}>
            <Typography variant="h5" align="center" sx={{ mb: -0.5 }}>
              Status
            </Typography>
            <Box
              display="flex"
              sx={{
                width: "100%",
                justifyContent: "center",
              }}
            >
              <ProgressIndicator {...ProgressStatus} />
            </Box>
          </Stack>
        </TableCell>
        <TableCell>
          <Stack direction="column" spacing={1}>
            <Box sx={{ display: "flex", gap: 1 }}>
              {rma.status === "AWAITING_RECEIPT" && (
                <Tooltip title="Receive RMA">
                  <IconButton size="small" onClick={() => onClickReceive()}>
                    <ItemIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {!["COMPLETED"].includes(rma.status) && (
                <Tooltip title="Edit RMA">
                  <IconButton size="small" onClick={() => onClickEdit()}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="View RMA">
                <IconButton size="small" onClick={() => onClickView()}>
                  <ViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export const PageRMAList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { user } = useAuth();

  const [error, setError] = useState<string | null>(null);

  const [targetCF, setTargetCF] = useState<string>("");
  const [targetLot, setTargetLot] = useState<string>("");
  const [targetCust, setTargetCust] = useState<string>("");

  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });

  const [orderBy, setOrderBy] = useState<string>("date_opened");
  const [order, setOrder] = useState<"asc" | "desc">("desc");

  const [formOpen, setFormOpen] = useState<boolean>(false);
  const [formMode, setFormMode] = useState<RMAFormMode>("create");
  const [formData, setformData] = useState<RMA_FULL | null>(null);

  function handleFormDialogOpen(mode: RMAFormMode, data: RMA_FULL | null) {
    setFormMode(mode);
    setformData(data);
    setFormOpen(true);
  }

  function handleBatchCreate(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) {
    handleFormDialogOpen("createbatch", null);
  }

  function handleSingleCreate(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) {
    handleFormDialogOpen("create", null);
  }

  function handleDialogClose() {
    setFormOpen(false);
    setformData(null);
  }

  const sorts: DynamicRequestSort[] = useMemo(() => {
    switch (orderBy) {
      case "number":
        return [
          {
            table: "returned_materials",
            alt_name: "rma",
            field: "number",
            asc: order === "asc",
          },
        ];
      case "customer":
        return [
          {
            table: "customers",
            alt_name: "cust",
            field: "name",
            asc: order === "asc",
          },
          {
            table: "returned_materials",
            alt_name: "rma",
            field: "date_opened",
            asc: false,
          },
        ];
      case "date_opened":
        return [
          {
            table: "returned_materials",
            alt_name: "rma",
            field: "date_opened",
            asc: order === "asc",
          },
        ];
      case "date_received":
        return [
          {
            table: "returned_materials",
            alt_name: "rma",
            field: "date_received",
            asc: order === "asc",
          },
        ];
      case "date_closed":
        return [
          {
            table: "returned_materials",
            alt_name: "rma",
            field: "date_closed",
            asc: order === "asc",
          },
        ];
      default:
        return [];
    }
  }, [order, orderBy]);

  const filters: DynamicRequestFilter[][] = useMemo(() => {
    const filts: DynamicRequestFilter[][] = [];
    if (targetCust !== "") {
      filts[0] = [];
      filts[1] = [];
      filts[0].push({
        table: "customers",
        alt_name: "cust",
        field: "name",
        operator: "contains",
        value: targetCust,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      filts[1].push({
        table: "customers",
        alt_name: "cust",
        field: "name",
        operator: "contains",
        value: targetCust,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    if (targetCF !== "") {
      if (filts[0] === undefined) {
        filts[0] = [];
      }
      filts[0].push({
        table: "customer_feedback",
        alt_name: "cf",
        field: "cf_number",
        operator: "contains",
        value: targetCF,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      if (filts[1] === undefined) {
        filts[1] = [];
      }
      filts[1].push({
        table: "customer_feedback",
        alt_name: "cf",
        field: "gl_cf_num",
        operator: "contains",
        value: targetCF,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    if (targetLot !== "") {
      if (filts[0] === undefined) {
        filts[0] = [];
      }
      filts[0].push({
        table: "inventory_lots",
        alt_name: "lot",
        field: "lot_number",
        operator: "contains",
        value: targetLot,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
      if (filts[1] === undefined) {
        filts[1] = [];
      }
      filts[1].push({
        table: "inventory_lots",
        alt_name: "lot",
        field: "lot_number",
        operator: "contains",
        value: targetLot,
        is_not: false,
        inclusive_start: false,
        inclusive_end: false,
        case_insensitive: true,
      });
    }
    return filts;
  }, [targetLot, targetCust, targetCF]);

  const {
    data: rmaData,
    isLoading: rmaLoading,
    error: queryError,
    isPlaceholderData: rmaPlaceholder,
  } = useTSQDynamicList({
    page: pagination.page,
    page_size: pagination.size,
    primary: "returned_materials",
    alt_name: "rma",
    primary_fields: [],
    joins: [
      {
        table: "customer_feedback",
        alt_name: "cf",
        fields: ["id", "cf_number", "cf_label", "gl_cf_num", "customer_id"],
        condition: {
          remote_table: "returned_materials",
          alt_name: "rma",
          remote_field: "cf_id",
          local_field: "id",
        },
        outer: true,
      },
      {
        table: "customers",
        alt_name: "cust",
        fields: ["name", "code"],
        condition: {
          remote_table: "returned_materials",
          alt_name: "rma",
          remote_field: "cust_id",
          local_field: "id",
        },
        outer: false,
      },
      {
        table: "items",
        alt_name: "item",
        fields: ["id", "catalog_name", "name", "number"],
        condition: {
          remote_table: "returned_materials",
          alt_name: "rma",
          remote_field: "item_id",
          local_field: "id",
        },
        outer: false,
      },
      {
        table: "inventory_lots",
        alt_name: "lot",
        fields: ["id", "lot_number", "item_id"],
        condition: {
          remote_table: "returned_materials",
          alt_name: "rma",
          remote_field: "lot_id",
          local_field: "id",
        },
        outer: true,
      },
      {
        table: "control_modules",
        alt_name: "loaner",
        fields: ["id", "number"],
        condition: {
          remote_table: "returned_materials",
          alt_name: "rma",
          remote_field: "loaner_id",
          local_field: "id",
        },
        outer: true,
      },
      {
        table: "control_modules",
        alt_name: "repl",
        fields: ["id", "number"],
        condition: {
          remote_table: "returned_materials",
          alt_name: "rma",
          remote_field: "replacement_id",
          local_field: "id",
        },
        outer: true,
      },
      {
        table: "shipment_records",
        alt_name: "inship",
        fields: ["id", "status"],
        condition: {
          remote_table: "returned_materials",
          alt_name: "rma",
          remote_field: "shipment_inbound",
          local_field: "id",
        },
        outer: true,
      },
      {
        table: "shipment_records",
        alt_name: "loanship",
        fields: ["id", "status"],
        condition: {
          remote_table: "returned_materials",
          alt_name: "rma",
          remote_field: "shipment_loaner",
          local_field: "id",
        },
        outer: true,
      },
      {
        table: "shipment_records",
        alt_name: "retship",
        fields: ["id", "status"],
        condition: {
          remote_table: "returned_materials",
          alt_name: "rma",
          remote_field: "shipment_return",
          local_field: "id",
        },
        outer: true,
      },
    ],
    filters: filters,
    sorts: sorts,
    subqueries: [],
  });

  const [rmaObjs, recCount]: [RMA_FULL[], number] = useMemo(() => {
    const rmaObjs: RMA_FULL[] = [];
    if (!!rmaData?.items && rmaData.items.length > 0) {
      console.log("RMA RESPONSE", rmaData);
      rmaData.items.forEach((val) => {
        console.log("CF Value", val);
        rmaObjs.push({
          cf_id: val["cf.id"],
          nuc_cf_num: val["cf.cf_number"]
            ? `NUC-CF-${val["cf.cf_number"].toString().padStart(4, "0")}`
            : null,
          nuc_cf_label: val["cf.cf_label"],
          gl_cf: val["cf.gl_cf_num"],
          cf_cust_id: val["cf.customer_id"],
          cust_id: val["cust.id"],
          cust_name: val["cust.name"],
          cust_code: val["cust.code"],
          item_id: val["item.id"],
          item_catalog: val["item.catalog_name"],
          item_num: val["item.number"],
          item_name: val["item.name"],
          lot_id: val["lot.id"],
          lot_num: val["lot.lot_number"],
          lot_item_id: val["lot.item_id"],
          loaner_id: val["loaner.id"],
          loaner_num: val["loaner.number"],
          replacement_id: val["repl.id"],
          replacement_num: val["repl.number"],
          shipment_inbound: val["inship.id"],
          shipment_inbound_status: val["inship.status"],
          shipment_loaner: val["loanship.id"],
          shipment_loaner_status: val["loanship.status"],
          shipment_return: val["retship.id"],
          shipment_return_status: val["retship.status"],
          number: parseInt(val["rma.number"]),
          reason_other: val["rma.reason_other"],
          rma_details: val["rma.rma_details"],
          quantity: val["rma.quantity"],
          quantity_received: val["rma.quantity_received"],
          date_opened: new Date(val["rma.date_opened"]),
          date_received: val["rma.date_received"]
            ? new Date(val["rma.date_received"])
            : null,
          date_inspected: val["rma.date_inspected"]
            ? new Date(val["rma.date_inspected"])
            : null,
          date_estimated_completion: val["rma.date_estimated_completion"]
            ? new Date(val["rma.date_estimated_completion"])
            : null,
          date_closed: val["rma.date_closed"]
            ? new Date(val["rma.date_closed"])
            : null,
          status: val["rma.status"],
          reason: val["rma.reason"],
          contact: val["rma.contact"],
          address: val["rma.address"],
          return_addr: val["rma.return_addr"],
          id: val["rma.id"],
          created_at: val["rma.created_at"],
          updated_at: val["rma.updated_at"],
          is_record_active: val["rma.is_record_active"],
          is_voided: val["rma.is_voided"],
          is_holding: val["rma.is_holding"],
        });
      });
    } else if (isDev) {
      console.log("ISDEV DATA FALLBACK", {
        response: rmaData,
        isDev: isDev,
      });
      const fallBack: RMA_FULL[] = [
        {
          cf_id: null,
          nuc_cf_num: null,
          nuc_cf_label: null,
          gl_cf: null,
          cf_cust_id: null,
          cust_id: "cust_id",
          cust_name: "CUSTOMER NAME",
          cust_code: "C####",
          item_id: "item_id",
          item_catalog: "TGCATALOG",
          item_num: "FGS-####",
          item_name: "ITEM NAME",
          lot_id: null,
          lot_num: null,
          lot_item_id: null,
          loaner_id: null,
          loaner_num: null,
          replacement_id: null,
          replacement_num: null,
          shipment_inbound: null,
          shipment_inbound_status: null,
          shipment_loaner: null,
          shipment_loaner_status: null,
          shipment_return: null,
          shipment_return_status: null,
          number: 1,
          reason_other: null,
          rma_details: "THESE ARE DETAILS",
          quantity: 1,
          quantity_received: 0,
          date_opened: new Date("6/1/26"),
          date_received: null,
          date_inspected: null,
          date_estimated_completion: null,
          date_closed: null,
          status: "AWAITING_CENTESE_CONFIRMATION",
          reason: "SERVICE",
          contact: {
            name: "CONTACT",
            email: "email@email.com",
            phone: null,
          },
          address: {
            attn: "CONTACT",
            addressee: "COMPANY RMA",
            line_1: "12345 Place Street",
            line_2: null,
            line_3: null,
            city: "Omaha",
            state: "NE",
            country: "US",
            zip: "123456",
            phone: null,
            email: null,
          },
          return_addr: null,
          id: "rma_id",
          created_at: new Date("6/1/26"),
          updated_at: new Date("6/1/26"),
          is_record_active: true,
          is_voided: false,
          is_holding: false,
        },
        {
          cf_id: null,
          nuc_cf_num: null,
          nuc_cf_label: null,
          gl_cf: null,
          cf_cust_id: null,
          cust_id: "cust_id",
          cust_name: "CUSTOMER NAME",
          cust_code: "C####",
          item_id: "item_id",
          item_catalog: "TGCATALOG",
          item_num: "FGS-####",
          item_name: "ITEM NAME",
          lot_id: "lot_id",
          lot_num: "LOT NUMBER",
          lot_item_id: "item_id",
          loaner_id: null,
          loaner_num: null,
          replacement_id: null,
          replacement_num: null,
          shipment_inbound: "inbound_id",
          shipment_inbound_status: "DELIVERED",
          shipment_loaner: null,
          shipment_loaner_status: null,
          shipment_return: null,
          shipment_return_status: null,
          number: 2,
          reason_other: "It kept speaking to me in my dreams.",
          rma_details: "THESE ARE SOME OTHER DETAILS",
          quantity: 1,
          quantity_received: 1,
          date_opened: new Date("6/2/26"),
          date_received: new Date("6/3/26"),
          date_inspected: null,
          date_estimated_completion: null,
          date_closed: null,
          status: "AWAITING_INSPECTION",
          reason: "OTHER",
          contact: {
            name: "CONTACT",
            email: "email@email.com",
            phone: null,
          },
          address: {
            attn: "CONTACT",
            addressee: "COMPANY RMA",
            line_1: "12345 Place Street",
            line_2: null,
            line_3: null,
            city: "Omaha",
            state: "NE",
            country: "US",
            zip: "123456",
            phone: null,
            email: null,
          },
          return_addr: {
            attn: "CONTACT",
            addressee: "COMPANY RMA",
            line_1: "54321 Some Other Place Street",
            line_2: null,
            line_3: null,
            city: "Omaha",
            state: "NE",
            country: "US",
            zip: "123456",
            phone: null,
            email: null,
          },
          id: "rma_id",
          created_at: new Date("6/2/26"),
          updated_at: new Date("6/3/26"),
          is_record_active: true,
          is_voided: false,
          is_holding: false,
        },
        {
          cf_id: "cf_id",
          nuc_cf_num: "NUC-CF-123",
          nuc_cf_label: "CF LABEL",
          gl_cf: "CF-123",
          cf_cust_id: "cust_id",
          cust_id: "cust_id",
          cust_name: "CUSTOMER NAME",
          cust_code: "C####",
          item_id: "item_id",
          item_catalog: "TGCATALOG",
          item_num: "FGS-####",
          item_name: "ITEM NAME",
          lot_id: "lot_id",
          lot_num: "LOT NUMBER",
          lot_item_id: "item_id",
          loaner_id: "loaner_id",
          loaner_num: "LOANER NUM",
          replacement_id: null,
          replacement_num: null,
          shipment_inbound: "inbound_id",
          shipment_inbound_status: "IN_TRANSIT",
          shipment_loaner: null,
          shipment_loaner_status: null,
          shipment_return: null,
          shipment_return_status: null,
          number: 3,
          reason_other: null,
          rma_details: "THESE ARE SOME OTHER DETAILS",
          quantity: 1,
          quantity_received: 1,
          date_opened: new Date("6/4/26"),
          date_received: null,
          date_inspected: null,
          date_estimated_completion: null,
          date_closed: null,
          status: "AWAITING_RECEIPT",
          reason: "SERVICE",
          contact: {
            name: "CONTACT",
            email: "email@email.com",
            phone: null,
          },
          address: {
            attn: "CONTACT",
            addressee: "COMPANY RMA",
            line_1: "12345 Place Street",
            line_2: null,
            line_3: null,
            city: "Omaha",
            state: "NE",
            country: "US",
            zip: "123456",
            phone: null,
            email: null,
          },
          return_addr: {
            attn: "CONTACT",
            addressee: "COMPANY RMA",
            line_1: "54321 Some Other Place Street",
            line_2: null,
            line_3: null,
            city: "Omaha",
            state: "NE",
            country: "US",
            zip: "123456",
            phone: null,
            email: null,
          },
          id: "rma_id",
          created_at: new Date("6/4/26"),
          updated_at: new Date("6/4/26"),
          is_record_active: true,
          is_voided: false,
          is_holding: false,
        },
        {
          cf_id: "cf_id",
          nuc_cf_num: "NUC-CF-123",
          nuc_cf_label: "CF LABEL",
          gl_cf: "CF-123",
          cf_cust_id: "cust_id",
          cust_id: "cust_id",
          cust_name: "CUSTOMER NAME",
          cust_code: "C####",
          item_id: "item_id",
          item_catalog: "TGCATALOG",
          item_num: "FGS-####",
          item_name: "ITEM NAME",
          lot_id: "lot_id",
          lot_num: "LOT NUMBER",
          lot_item_id: "item_id",
          loaner_id: "loaner_id",
          loaner_num: "LOANER NUM",
          replacement_id: "replacement_id",
          replacement_num: "REAPLCEMENT_NUM",
          shipment_inbound: "inbound_ship_id",
          shipment_inbound_status: "DELIVERED",
          shipment_loaner: "loaner_ship_id",
          shipment_loaner_status: "DELIVERED",
          shipment_return: "return_ship_id",
          shipment_return_status: "DELIVERED",
          number: 4,
          reason_other: null,
          rma_details: "THESE ARE SOME OTHER DETAILS",
          quantity: 1,
          quantity_received: 1,
          date_opened: new Date("6/5/26"),
          date_received: null,
          date_inspected: null,
          date_estimated_completion: null,
          date_closed: null,
          status: "COMPLETED",
          reason: "SERVICE",
          contact: {
            name: "CONTACT",
            email: "email@email.com",
            phone: null,
          },
          address: {
            attn: "CONTACT",
            addressee: "COMPANY RMA",
            line_1: "12345 Place Street",
            line_2: null,
            line_3: null,
            city: "Omaha",
            state: "NE",
            country: "US",
            zip: "123456",
            phone: null,
            email: null,
          },
          return_addr: {
            attn: "CONTACT",
            addressee: "COMPANY RMA",
            line_1: "54321 Some Other Place Street",
            line_2: null,
            line_3: null,
            city: "Omaha",
            state: "NE",
            country: "US",
            zip: "123456",
            phone: null,
            email: null,
          },
          id: "rma_id",
          created_at: new Date("6/5/26"),
          updated_at: new Date("6/5/26"),
          is_record_active: true,
          is_voided: false,
          is_holding: false,
        },
      ];
      console.log("FALLBACK", fallBack);
      return [fallBack, fallBack.length];
    }

    return [rmaObjs, rmaData?.total || 0];
  }, [rmaData]);

  const [displayProcessDialog, setDisplayProcessDialog] =
    useState<boolean>(false);
  const [processDialogError, setProcessDialogError] = useState<string | null>(
    null,
  );

  const handleProcessDialogClose = (
    event: any,
    reason: "backdropClick" | "escapeKeyDown",
  ) => {
    if (reason === "backdropClick") {
      return;
    }
    setDisplayProcessDialog(false);
    setProcessDialogError(null);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          RMAs
        </Typography>
        <Box display="flex" gap={0.5}>
          <SplitButton
            id="rma-create-split"
            options={[
              {
                id: "create-btn",
                text: "New RMA",
                variant: "contained",
                onClick: handleSingleCreate,
                startIcon: <AddIcon />,
              },
              {
                id: "copy-btn",
                text: "New RMA Batch",
                variant: "contained",
                onClick: handleBatchCreate,
                startIcon: <AddMultiple />,
              },
            ]}
            isSwitch={false}
            executeText="Create RMA"
          />
          {user?.is_superuser && (
            <Button
              variant="outlined"
              color="info"
              onClick={() => console.log(rmaObjs)}
            >
              DEBUG PRINT
            </Button>
          )}
        </Box>
      </Stack>

      {(error || queryError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || queryError?.message || "An unknown error occurred"}
        </Alert>
      )}

      {rmaPlaceholder && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading updated data...
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box display="flex" flexWrap={"wrap"} gap={1}>
          <FilterBox
            finalSet={setTargetCust}
            placeholder="Filter By Customer Name/Code"
          />
          <FilterBox
            finalSet={setTargetLot}
            placeholder="Filter By Lot Number"
          />
          <FilterBox
            finalSet={setTargetCF}
            placeholder="Filter By Customer Name/Code"
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <TablePagination
          component="div"
          count={recCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sortDirection={orderBy === "number" ? order : false}
                width="15%"
              >
                <TableSortLabel
                  active={orderBy === "number"}
                  direction={orderBy === "number" ? order : "asc"}
                  onClick={() => handleSort("number")}
                >
                  RMA Number
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "customer" ? order : false}
                width="15%"
              >
                <TableSortLabel
                  active={orderBy === "customer"}
                  direction={orderBy === "customer" ? order : "asc"}
                  onClick={() => handleSort("customer")}
                >
                  Customer / Contact
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "date_opened" ? order : false}
                width="15%"
              >
                <TableSortLabel
                  active={orderBy === "date_opened"}
                  direction={orderBy === "date_opened" ? order : "asc"}
                  onClick={() => handleSort("date_opened")}
                >
                  Dates
                </TableSortLabel>
              </TableCell>
              <TableCell width="45%" sx={{ minWidth: "45%", maxWidth: "45%" }}>
                Details
              </TableCell>
              <TableCell width="10%">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rmaLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rmaObjs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    {filters.length > 0
                      ? "No RMA Records found matching your search."
                      : "No RMA Records found."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rmaObjs.map((rma) => (
                <RMARow
                  rma={rma}
                  onClickReceive={() => handleFormDialogOpen("receive", rma)}
                  onClickView={() => handleFormDialogOpen("view", rma)}
                  onClickEdit={() => handleFormDialogOpen("edit", rma)}
                />
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={recCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>
      <RMAHandlerForm
        open={formOpen}
        mode={formMode}
        rmaData={formData}
        user={user}
        onClose={handleDialogClose}
        onSuccess={() => {}}
      />
      {/* <Dialog
        open={displayProcessDialog}
        onClose={(e, r) => {
          handleProcessDialogClose(e, r);
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Stack
            direction="column"
            width="100%"
            justifyContent={"center"}
            gap={3}
          >
            {!!processDialogError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {processDialogError}
              </Alert>
            )}
            <Box
              display="flex"
              gap={2}
              justifyContent="space-evenly"
              width="50%"
            >
              <Typography variant="h5">Approval</Typography>
              {approveComplaintMutation.isPending ? (
                <CircularProgress />
              ) : approveComplaintMutation.data?.success ? (
                <CheckIcon color="primary" />
              ) : (
                <CancelIcon color="error" />
              )}
            </Box>
            <Box
              display="flex"
              gap={2}
              justifyContent="space-evenly"
              width="50%"
            >
              <Typography variant="h5">Post Processing</Typography>
              {approveComplaintMutation.isPending ||
              postProcComplaintMutation.isPending ? (
                <CircularProgress />
              ) : postProcComplaintMutation.data?.success ? (
                <CheckIcon color="primary" />
              ) : (
                <CancelIcon color="error" />
              )}
            </Box>
            {approveComplaintMutation.isPending ||
            postProcComplaintMutation.isPending ? (
              <Typography variant="h5">
                Page will return to Complaint List View upon request
                completion...
              </Typography>
            ) : !(
                !!approveComplaintMutation.data?.success &&
                !!postProcComplaintMutation.data?.success
              ) ? (
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  handleProcessDialogClose(null, "escapeKeyDown");
                }}
              >
                Close
              </Button>
            ) : (
              <Typography variant="h5">
                Requests completed, returning to Complaint List View mode...
              </Typography>
            )}
          </Stack>
        </DialogContent>
      </Dialog> */}
    </Box>
  );
};
